import React, { useEffect, useState } from "react";
import { makeStyles, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useBreakpoints, useCompanyDetails, useKeycloakFunctions } from "../../../hooks";
import { useI18N, useLocation } from "../../../common/common";
import CustomDialog from "../../dialog/marketplace/common/CustomDialog";
import { INVOICES_CREDITS, MARKET_PLACE_BUY, MEMBERSHIP } from "../../../common/routes";
import { ellipsis } from "../../../common/constant";

const useStyles = makeStyles((theme) => ({
    messageLabel: {
        marginTop: theme.spacing(5),
    },
    messageLabelMobile: {
        marginTop: theme.spacing(3),
    },
    unauthorizedModalButton: {
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: "100%"
    },
}));

const bookingErrorType = {
    "ApartmentNotAvailable": 0,
    "BookingStop": 1,
    "MemberDeceased": 2,
    "ArrivingToday": 3,
    "PurchaseLimitReached": 4,
    "NoShareNotAllowedToBuyPoints": 5,
    "PointsPlatformDown": 6,
    "UnknownError": 7,
    "IsAdmin": 8,
    "IsUnauthorized": 9,
    "ProspectForbidden": 10
};

const BookingErrorDialog = ({isOpen, onClose, type, resortPhoneNumber, onOpenSearchBar, buttonText}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen} = useBreakpoints();
    const {navigate} = useLocation();
    const {companyDetails} = useCompanyDetails();
    const {login} = useKeycloakFunctions();

    const [modal, setModal] = useState(null);

    const dialog = {
        [bookingErrorType.ApartmentNotAvailable]: {
            type: "not_available",
            icon: "/images/not_available.svg",
            title: i18n("view.reservations.not_available_apartment_title"),
            description: i18n("view.reservations.not_available_apartment_description"),
            button: "view.reservations.not_available_apartment_button",
            buttonContained: true,
            color: "primary",
            xButton: false,
            disableBackdropClick: true,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.BookingStop]: {
            type: "invoice_error",
            icon: "/images/not_available.svg",
            title: i18n("view.reservations.invoice_error_title"),
            description: (
                <>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                        style={{fontWeight: "bold"}}
                    >
                        {i18n("view.reservations.invoice_error_description0")}
                    </Typography>
                    <Typography
                        variant="body1"
                    >
                        {i18n("view.reservations.invoice_error_description1")}
                        <span
                            style={{fontWeight: "bold"}}
                        >
                            {" " + companyDetails?.contactDetails.serviceNumber}
                        </span>
                    </Typography>
                </>
            ),
            button: "view.reservations.invoice_error_button",
            buttonContained: true,
            color: "primary",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {navigate(INVOICES_CREDITS);}
        },
        [bookingErrorType.MemberDeceased]: {
            type: "member_deceased",
            icon: "/images/not_available.svg",
            title: i18n("view.profile.points_shares.sell_points.error.account_blocked.title"),
            description: i18n("view.profile.points_shares.sell_points.error.account_blocked.description"),
            button: "common.label.close",
            buttonContained: true,
            color: "primary",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.ArrivingToday]: {
            type: "arriving_today",
            icon: "/images/calendar.svg",
            title: i18n("view.reservations.arriving_today_title"),
            description: (
                <>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                    >
                        {i18n("view.reservations.arriving_today_description")}
                    </Typography>

                    <Typography
                        variant="body1"
                    >
                        {i18n("view.reservations.arriving_today_description1")}
                        <span
                            style={{fontWeight: "bold"}}
                        >
                            {" " + resortPhoneNumber}
                        </span>
                    </Typography>
                </>
            ),
            button: buttonText ?? "view.reservations.arriving_today_button",
            buttonContained: false,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {
                handleClose();
                if (typeof onOpenSearchBar === "function") {
                    onOpenSearchBar();
                }
            }
        },
        [bookingErrorType.PurchaseLimitReached]: {
            type: "points_error1",
            icon: "/images/points_error.svg",
            title: `${i18n("view.reservations.points_error_title")} ${i18n("common.label.points")} ${i18n("view.reservations.points_error_title1")}`,
            description: (
                <>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                    >
                        {i18n("view.reservations.points_error_description2")}
                    </Typography>
                    <Typography
                        variant="body1"
                    >
                        {i18n("view.reservations.points_error_description1")}
                    </Typography>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                    >
                        {i18n("view.reservations.points_error_description3")}
                        <span
                            style={{fontWeight: "bold"}}
                        >
                            {" " + companyDetails?.contactDetails.serviceNumber}
                        </span>
                    </Typography>
                </>
            ),
            button: "common.label.close",
            buttonContained: false,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.NoShareNotAllowedToBuyPoints]: {
            type: "points_error2",
            icon: "/images/points_error.svg",
            title: `${i18n("view.reservations.points_error_title")} ${i18n("common.label.points")} ${i18n("view.reservations.points_error_title1")}`,
            description: (
                <>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                    >
                        {i18n("view.reservations.points_error_description1")}
                    </Typography>
                    <Typography
                        variant="body1"
                        className={clsx({[classes.messageLabelMobile]: isExtraSmallScreen(), [classes.messageLabel]: !isExtraSmallScreen()})}
                    >
                        {i18n("view.reservations.points_error_description3")}
                        <span
                            style={{fontWeight: "bold"}}
                        >
                            {" " + companyDetails?.contactDetails.serviceNumber}
                        </span>
                    </Typography>
                </>
            ),
            button: "common.label.close",
            buttonContained: false,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.PointsPlatformDown]: {
            type: "maintenance_error",
            icon: "/images/car_error.svg",
            title: i18n("view.reservations.maintenance_error_title"),
            description: i18n("view.reservations.maintenance_error_description"),
            button: "common.label.close",
            buttonContained: false,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.UnknownError]: {
            type: "system_error",
            icon: "/images/car_error.svg",
            title: i18n("common.label.could_not_complete_request"),
            description: i18n("common.label.system_plunge"),
            button: "common.label.please_try_again",
            buttonContained: false,
            color: "black",
            xButton: false,
            disableBackdropClick: true,
            handleButtonAction: () => {window.location.reload();}
        },
        [bookingErrorType.IsAdmin]: {
            type: "admin_cannot_perform_action",
            icon: "/images/admin-cannot-perform-action-icon.svg",
            title: i18n("view.reservations.logged_in_as_admin"),
            description: i18n("view.reservations.thats_why_cannot_perform_action"),
            button: "common.label.close",
            buttonContained: false,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {handleClose();}
        },
        [bookingErrorType.IsUnauthorized]: {
            type: "admin_cannot_perform_action",
            icon: "/images/log-in-to-finalise-reservation.svg",
            title: `${i18n("view.reservations.one_last_step")}${ellipsis}`,
            description: i18n("view.reservations.you_need_to_be_logged_in"),
            button: "common.label.login",
            secondButton: true,
            buttonClass: classes.unauthorizedModalButton,
            secondButtonOptions: {
                title: "common.label.explore_membership",
                class: classes.unauthorizedModalButton
            },
            buttonContained: true,
            color: "black",
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {login();},
            handleSecondButtonAction: () => {navigate(MEMBERSHIP);}
        },
        [bookingErrorType.ProspectForbidden]: {
            type: "prospect_forbidden",
            icon: "/images/offer-published-icon-no-check.svg",
            title: i18n("dialog.prospect_forbidden.next_steps"),
            description: i18n("dialog.prospect_forbidden.need_to_own_shares"),
            button: "view.buy_a_share.card_buy_on_your_own.go_to_the_marketplace",
            secondButton: false,
            buttonContained: true,
            xButton: true,
            disableBackdropClick: false,
            handleCloseDialog: () => {handleClose();},
            handleButtonAction: () => {navigate(MARKET_PLACE_BUY);},
        }
    };

    useEffect(() => {
        if (type) {
            setModal(dialog[type]);
        } else {
            setModal(null);
        }
    }, [type]);


    const handleClose = () => {
        setModal(null);
        onClose();
    };

    return (
        <>
            {modal && (
                <CustomDialog
                    isOpen={isOpen}
                    isMobile={isExtraSmallScreen()}
                    buttonContained={modal.buttonContained}
                    onCloseDialog={modal.handleCloseDialog}
                    title={modal.title}
                    color={modal.color}
                    description={modal.description}
                    cta={i18n(modal.button)}
                    onButtonClick={modal.handleButtonAction}
                    onSecondButtonClick={modal?.handleSecondButtonAction}
                    imgSrc={modal.icon}
                    imgAlt={"error"}
                    xButton={modal.xButton}
                    disableBackdropClick={modal?.disableBackdropClick}
                    secondButton={modal?.secondButton}
                    secondButtonOptions={modal?.secondButtonOptions}
                    buttonStartIcon={modal?.buttonStartIcon}
                    buttonClass={modal?.buttonClass}
                    data-testid={modal?.type}
                />
            )}
        </>
    );
};

BookingErrorDialog.propTypes = {
    isOpen: PropTypes.bool,
    type: PropTypes.number,
    onClose: PropTypes.func,
    resortPhoneNumber: PropTypes.string,
    onOpenSearchBar: PropTypes.func,
    buttonText: PropTypes.string
};

export default BookingErrorDialog;
export { bookingErrorType };
